/**
 * Daum (Kakao) Postcode service — 다음 우편번호 서비스.
 *
 * A front end over the government's official 도로명주소 database. It is free,
 * needs no API key and no registration, and is what most Korean storefronts use.
 * The customer searches and *picks* an address, so what we store is guaranteed
 * to be a real, deliverable address with a correct 5-digit postcode.
 *
 * Docs: https://postcode.map.daum.net/guide
 */

const SCRIPT_SRC =
  'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

let loader = null;

/** Injects the Daum script once; repeated calls reuse the same promise. */
export function loadPostcodeScript() {
  if (window.daum && window.daum.Postcode) return Promise.resolve();
  if (loader) return loader;

  loader = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('postcode script failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      loader = null; // let a later attempt retry
      reject(new Error('postcode script failed'));
    };
    document.head.appendChild(s);
  });

  return loader;
}

/**
 * Turns a raw Daum result into the shape our API stores.
 * Mirrors Daum's own reference implementation for composing the display line.
 */
export function toAddress(data) {
  const road = data.roadAddress || data.autoRoadAddress || '';
  const jibun = data.jibunAddress || data.autoJibunAddress || '';

  // The customer may have searched by road name (R) or by lot number (J).
  let base = data.userSelectedType === 'J' ? jibun : road;

  // For road addresses Daum recommends appending the legal dong and, for
  // apartments, the building name — "테헤란로 123 (역삼동, 아무개빌딩)".
  if (data.userSelectedType !== 'J') {
    let extra = '';
    if (data.bname && /[동|로|가]$/g.test(data.bname)) extra += data.bname;
    if (data.buildingName && data.apartment === 'Y') {
      extra += extra ? `, ${data.buildingName}` : data.buildingName;
    }
    if (extra) base += ` (${extra})`;
  }

  return {
    postal_code: data.zonecode,          // 5-digit
    road_address: road,
    jibun_address: jibun,
    address_english: data.roadAddressEnglish || data.addressEnglish || '',
    sido: data.sido || '',
    sigungu: data.sigungu || '',
    bname: data.bname || '',
    building_name: data.buildingName || '',
    address_source: 'daum_postcode',

    // What we show the customer and print on the label.
    address_line: base,
    street: base,
  };
}

/**
 * Opens the postcode popup.
 * Resolves with a normalised address, or `null` if the customer closed it.
 */
export async function searchPostcode() {
  await loadPostcodeScript();

  return new Promise((resolve) => {
    let picked = null;
    new window.daum.Postcode({
      oncomplete: (data) => {
        picked = toAddress(data);
      },
      // Fires on close whether they picked something or hit the X.
      onclose: () => resolve(picked),
    }).open();
  });
}
